import { GET_COLUMNS } from '../columns/columns.store';
import { GET_GROUPING, SET_GROUPING } from '../columns/grouping.store';
import { CLEAR_COLLAPSED_GROUPS } from '../table.ui.store';

import { TPointCustomField } from '@project/view-models';
import { decodeBase64Text } from 'src/app/project/components/input/rich-text/decode-base64-text';
import { generatePlainText } from 'src/app/project/components/input/rich-text/generate-plain-text';
import { safelyParseJSON } from 'src/app/project/components/input/rich-text/safely-parse-json';
import { translate } from 'src/app/project/features/translate/translate';
import { TPoint } from 'src/app/project/modules/points/points.model';
import { EBasicField } from 'src/app/project/shared/enums/basic-fields-enums';
import { ECustomFieldType } from '../../../custom-fields/custom-field-types-enums';
import { TCustomField } from '../../../custom-fields/custom-fields.model';
import { GET_CUSTOM_FIELDS } from '../../../custom-fields/custom-fields.store';
import { GET_WORKSPACES } from '../../../workspace/workspace.store';
import { TColumn } from '../columns/column.model';
import { findPropertyPath } from '../properties';
import { getEmptyGroupName } from './empty-group-name';
import { TGroupedPoints } from './group-model';
import { setPointsInOrder } from './set-points-in-order';
import { sortGroups } from './sort-groups';
import { transformCustomFieldValue } from './transform-custom-field-value';
import { transformDefaultValue } from './transform-default-value';
import { transformValue } from './transform-value';

type TGroupKey = { value: string; category: string; hiddenValue: string };

export const generateGrouping = (): void => {
  SET_GROUPING(
    GET_COLUMNS()
      .filter((_column) => _column.groupOrder)
      .sort((_a, _b) => _a.groupIndex - _b.groupIndex),
  );
  CLEAR_COLLAPSED_GROUPS();
};

export function groupTable({
  _points,
  pagination,
}: {
  _points: TPoint[];
  pagination: boolean;
}): TGroupedPoints[] {
  const grouping = GET_GROUPING();
  const column = grouping[0];
  const tablePoints = _points;
  const pointIndexById: Map<string, number> = new Map(
    tablePoints.map((point, index) => [point._id, index]),
  );

  const groupedPointIndexes = groupPoints({
    _points: tablePoints,
    column,
    pointIndexById,
    idPrefix: '',
    parentOption: '',
    pagination,
  });

  if (grouping[1]) {
    groupedPointIndexes.forEach((_groupedPointIndexes) => {
      _groupedPointIndexes.group = groupPoints({
        _points: _groupedPointIndexes.pointObjects,
        column: grouping[1],
        pointIndexById,
        parentOption: _groupedPointIndexes.value,
        idPrefix: _groupedPointIndexes.id,
        pagination,
      });

      if (grouping[2]) {
        _groupedPointIndexes.group.forEach((_group) => {
          _group.group = groupPoints({
            _points: _group.pointObjects,
            column: grouping[2],
            pointIndexById,
            idPrefix: _group.id,
            parentOption: _group.value,
            pagination,
          });
        });
      }
    });
  }

  setPointsInOrder(groupedPointIndexes, tablePoints);

  return groupedPointIndexes;
}

export function groupPoints({
  _points,
  column,
  pointIndexById,
  idPrefix,
  pagination,
  parentOption,
}: {
  _points: TPoint[];
  column: TColumn;
  pointIndexById: Map<string, number>;
  idPrefix: string;
  pagination: boolean;
  parentOption?: string;
}): TGroupedPoints[] {
  let groupedPointIndexes: TGroupedPoints[] = [];
  const valueIndexes = {};
  const emptyGroupValue = getEmptyGroupName();

  _points.forEach((_point) => {
    const index = pointIndexById.get(_point._id) ?? -1;
    const { value, category, hiddenValue } = resolveGroupKey(_point, column, emptyGroupValue);

    if (!Object.prototype.hasOwnProperty.call(valueIndexes, value)) {
      valueIndexes[value] = groupedPointIndexes.length;
    }

    if (!groupedPointIndexes[valueIndexes[value]]) {
      groupedPointIndexes[valueIndexes[value]] = {
        points: [],
        value,
        category,
        hiddenValue,
        parentOption,
        pointObjects: [],
        id: idPrefix + '-' + value,
      };
    }

    groupedPointIndexes[valueIndexes[value]].points.push(index);
    groupedPointIndexes[valueIndexes[value]].pointObjects.push(_point);
  });

  if (!pagination) {
    groupedPointIndexes = groupedPointIndexes.sort((_a, _b) => sortGroups(column, _a, _b));
  }

  return groupedPointIndexes;
}

function resolveBasicField(_point: TPoint, column: TColumn, propertyPath: string): TGroupKey {
  const properties = propertyPath.split('.');
  const value = transformValue(column.name, _point[properties[0]]);
  let category: string = column.name;
  let hiddenValue: string = _point[properties[0]] ? `${_point[properties[0]]}` : 'UNSPECIFIED';

  switch (column.name) {
    case EBasicField.CREATED:
      category = propertyPath;
      hiddenValue = `${_point[properties[0]]?.createdEpochMillis}`;
      break;
    case EBasicField.UPDATED:
      category = propertyPath;
      hiddenValue = `${_point[properties[0]].updatedEpochMillis}`;
      break;
    case 'Flag':
      category = 'flagged';
      break;
    case EBasicField.CREATED_BY:
      category = 'header.createdby.id';
      hiddenValue = _point[properties[0]]?.createdBy?.id ?? '';
      break;
    case EBasicField.ASSIGNEES:
    case EBasicField.TAGS:
      hiddenValue = `[${_point[properties[0]]}]`;
      break;
    case 'ID':
      category = 'sequencenumber';
  }

  return { value, category, hiddenValue };
}

function resolveWorkspaceField(_point: TPoint, column: TColumn): TGroupKey {
  const workspace = _point ? GET_WORKSPACES()[_point.workspaceRef.id] : null;
  const name = (
    column.name === EBasicField.SITE ? workspace?.siteName : workspace?.accountName
  ) as string;

  return {
    value: transformDefaultValue(name),
    category: column.name,
    hiddenValue: name,
  };
}

function resolveCustomField(_point: TPoint, column: TColumn, emptyGroupValue: string): TGroupKey {
  const customField = Object.values(GET_CUSTOM_FIELDS()).find(
    (cf) => cf.label === column.name && cf.workspaceId === _point.workspaceRef.id,
  );

  if (customField) {
    const { id, val, hiddenVal } = handleCustomFieldGroupedPoints({ _point, customField });
    return { value: val, category: id, hiddenValue: hiddenVal };
  }

  if (isCheckboxColumn(column)) {
    return { value: translate('no'), category: column.name, hiddenValue: '' };
  }

  return { value: emptyGroupValue, category: column.name, hiddenValue: '' };
}

function resolveGroupKey(_point: TPoint, column: TColumn, emptyGroupValue: string): TGroupKey {
  const propertyPath = findPropertyPath(column.name);
  if (propertyPath) {
    return resolveBasicField(_point, column, propertyPath);
  }
  if (column.name === EBasicField.SITE || column.name === EBasicField.ACCOUNT) {
    return resolveWorkspaceField(_point, column);
  }
  return resolveCustomField(_point, column, emptyGroupValue);
}

function isCheckboxColumn(column: TColumn): boolean {
  if (!column.customFieldIds?.length) {
    return false;
  }

  const customFields = GET_CUSTOM_FIELDS();

  return column.customFieldIds.some(
    (cfId) => customFields[cfId]?.type === ECustomFieldType.CHECKBOX,
  );
}

type CustomFieldGroup = {
  id: string;
  val: string;
  hiddenVal: string;
};

export function handleCustomFieldGroupedPoints({
  _point,
  customField,
}: {
  _point: TPoint;
  customField: TCustomField;
}): CustomFieldGroup {
  const pointCF: TPointCustomField = _point.customFieldsMap?.[customField.id];
  const id = customField.id;
  const val = transformCustomFieldValue(customField.id, pointCF);
  let hiddenVal = '';

  switch (customField.type) {
    case ECustomFieldType.CHECKBOX:
      hiddenVal = pointCF?.value === 'true' || pointCF?.value === true ? 'true' : '';
      break;
    case ECustomFieldType.DATE:
    case ECustomFieldType.COST:
    case ECustomFieldType.TIME:
    case ECustomFieldType.PERCENTAGE:
    case ECustomFieldType.NUMBERS:
      hiddenVal = pointCF?.value ?? '';
      break;
    case ECustomFieldType.RICHTEXT:
      hiddenVal = pointCF?.value
        ? generatePlainText(safelyParseJSON(decodeBase64Text(pointCF?.value))).trim()
        : '';
      break;
    case ECustomFieldType.MULTI_LIST:
      hiddenVal = pointCF?.selectedItemIds ? `[${pointCF?.selectedItemIds}]` : '';
      break;
    case ECustomFieldType.LIST:
      hiddenVal = pointCF?.idOfChosenElement ? `${pointCF?.idOfChosenElement}` : '';
      break;
    default:
      hiddenVal = transformCustomFieldValue(customField.id, pointCF) ?? customField.id;
  }

  return { id, val, hiddenVal };
}
